import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  Search, Users, AlertTriangle, Shield, ShieldAlert, ShieldCheck, 
  Eye, ChevronLeft, ChevronRight, Bell, User, Phone, Calendar,
  TrendingUp, Wallet, RefreshCw, FileText, Lock, Unlock, Plus, Loader2, Database,
  CreditCard, CheckCircle, XCircle, Clock, Image
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

const AdminKYCIdentitiesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('kyc');
  const [loading, setLoading] = useState(true);
  const [kycIdentities, setKycIdentities] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [stats, setStats] = useState({ multi_account_count: 0 });
  const [alerts, setAlerts] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // Only updates on submit
  const [multiAccountOnly, setMultiAccountOnly] = useState(false); // Default to show all
  const [statusFilter, setStatusFilter] = useState('');
  
  // Backfill
  const [backfillLoading, setBackfillLoading] = useState(false);
  
  // Detail view
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [kycDetail, setKycDetail] = useState(null);
  
  // Action dialogs
  const [restrictDialog, setRestrictDialog] = useState(false);
  const [restrictReason, setRestrictReason] = useState('');
  const [restrictDetails, setRestrictDetails] = useState('');
  const [noteDialog, setNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // PAN Verification State
  const [panList, setPanList] = useState([]);
  const [panLoading, setPanLoading] = useState(false);
  const [panPagination, setPanPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [panStatusFilter, setPanStatusFilter] = useState('pending');
  const [panSearchQuery, setPanSearchQuery] = useState('');
  const [selectedPan, setSelectedPan] = useState(null);
  const [panPhotoUrl, setPanPhotoUrl] = useState(null);
  const [panPhotoLoading, setPanPhotoLoading] = useState(false);
  const [rejectPanDialog, setRejectPanDialog] = useState(false);
  const [rejectPanReason, setRejectPanReason] = useState('');
  const [panActionLoading, setPanActionLoading] = useState(false);

  const fetchKYCIdentities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        multi_account_only: multiAccountOnly
      });
      
      if (activeSearch) params.append('search', activeSearch);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await adminAPI.getKYCIdentities(params.toString());
      setKycIdentities(response.data.kyc_identities || []);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching KYC identities:', error);
      toast.error('Failed to load KYC identities');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, multiAccountOnly, activeSearch, statusFilter]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await adminAPI.getKYCAlerts();
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  const fetchKYCDetail = async (kycIdentityId) => {
    setDetailLoading(true);
    setSelectedKYC(kycIdentityId);
    try {
      const response = await adminAPI.getKYCIdentityDetail(kycIdentityId);
      setKycDetail(response.data);
    } catch (error) {
      console.error('Error fetching KYC detail:', error);
      toast.error('Failed to load KYC details');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchKYCIdentities();
    fetchAlerts();
  }, [fetchKYCIdentities, fetchAlerts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery); // Only update activeSearch on submit
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRunBackfill = async () => {
    if (!window.confirm(
      'This will create KYC Identity records for all users who have completed Aadhaar verification.\n\n' +
      'This process will:\n' +
      '• Group users by Aadhaar number\n' +
      '• Create KYC Identity records\n' +
      '• Detect multi-account patterns (same Aadhaar, multiple phones)\n\n' +
      'Continue?'
    )) {
      return;
    }
    
    setBackfillLoading(true);
    try {
      const response = await adminAPI.runKYCBackfill();
      const { kyc_identities_created, users_linked, multi_account_detected, users_processed } = response.data;
      
      toast.success(
        `Backfill complete! ${kyc_identities_created} KYC identities created, ` +
        `${users_linked} users linked, ${multi_account_detected} multi-account patterns detected`
      );
      
      fetchKYCIdentities();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleRestrict = async (isBlock = false) => {
    if (!restrictReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    setActionLoading(true);
    try {
      if (isBlock) {
        await adminAPI.blockKYCIdentity(selectedKYC, { reason: restrictReason, details: restrictDetails });
        toast.success('KYC Identity blocked');
      } else {
        await adminAPI.restrictKYCIdentity(selectedKYC, { reason: restrictReason, details: restrictDetails });
        toast.success('KYC Identity restricted');
      }
      setRestrictDialog(false);
      setRestrictReason('');
      setRestrictDetails('');
      fetchKYCDetail(selectedKYC);
      fetchKYCIdentities();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async () => {
    setActionLoading(true);
    try {
      await adminAPI.unblockKYCIdentity(selectedKYC);
      toast.success('KYC Identity unblocked');
      fetchKYCDetail(selectedKYC);
      fetchKYCIdentities();
    } catch (error) {
      toast.error('Failed to unblock');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setActionLoading(true);
    try {
      await adminAPI.addKYCNote(selectedKYC, { note: newNote });
      toast.success('Note added');
      setNoteDialog(false);
      setNewNote('');
      fetchKYCDetail(selectedKYC);
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const markAlertRead = async (alertId) => {
    try {
      await adminAPI.markKYCAlertRead(alertId);
      fetchAlerts();
    } catch (error) {
      console.error('Error marking alert read:', error);
    }
  };

  // ==================== PAN VERIFICATION FUNCTIONS ====================
  
  const fetchPanList = useCallback(async () => {
    setPanLoading(true);
    try {
      const response = await adminAPI.getPANList(
        panPagination.page, 
        panPagination.limit, 
        panStatusFilter,
        panSearchQuery
      );
      setPanList(response.data.submissions || response.data.items || []);
      const paginationData = response.data.pagination || {};
      setPanPagination(prev => ({
        ...prev,
        total: paginationData.total_count || response.data.total || 0,
        pages: paginationData.total_pages || response.data.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load PAN submissions');
      console.error('Error fetching PAN list:', error);
    } finally {
      setPanLoading(false);
    }
  }, [panPagination.page, panPagination.limit, panStatusFilter, panSearchQuery]);

  useEffect(() => {
    if (activeTab === 'pan') {
      fetchPanList();
    }
  }, [activeTab, fetchPanList]);

  const handleViewPanPhoto = async (userId) => {
    setPanPhotoLoading(true);
    setPanPhotoUrl(null);
    try {
      const response = await adminAPI.getPANPhoto(userId);
      setPanPhotoUrl(response.data.photo_url || response.data.photo_base64);
    } catch (error) {
      toast.error('Failed to load PAN photo');
    } finally {
      setPanPhotoLoading(false);
    }
  };

  const handleApprovePan = async (userId) => {
    setPanActionLoading(true);
    try {
      await adminAPI.approvePAN(userId);
      toast.success('PAN approved successfully');
      setSelectedPan(null);
      setPanPhotoUrl(null);
      fetchPanList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve PAN');
    } finally {
      setPanActionLoading(false);
    }
  };

  const handleRejectPan = async () => {
    if (!rejectPanReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setPanActionLoading(true);
    try {
      await adminAPI.rejectPAN(selectedPan.user_id, rejectPanReason);
      toast.success('PAN rejected');
      setRejectPanDialog(false);
      setRejectPanReason('');
      setSelectedPan(null);
      setPanPhotoUrl(null);
      fetchPanList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject PAN');
    } finally {
      setPanActionLoading(false);
    }
  };

  const getPanStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3 mr-1" /> },
      verified: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      rejected: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3 h-3 mr-1" /> },
      not_submitted: { color: 'bg-gray-100 text-gray-800', icon: <CreditCard className="w-3 h-3 mr-1" /> }
    };
    const { color, icon } = config[status] || config.not_submitted;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        {icon}
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  // ==================== END PAN FUNCTIONS ====================

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      restricted: 'bg-yellow-100 text-yellow-800',
      blocked: 'bg-red-100 text-red-800'
    };
    const icons = {
      active: <ShieldCheck className="w-3 h-3 mr-1" />,
      restricted: <ShieldAlert className="w-3 h-3 mr-1" />,
      blocked: <Shield className="w-3 h-3 mr-1" />
    };
    return (
      <Badge className={`${colors[status] || 'bg-gray-100'} flex items-center`}>
        {icons[status]}
        {status?.toUpperCase()}
      </Badge>
    );
  };

  if (loading && kycIdentities.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-100 p-4" data-testid="admin-kyc-identities-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-amber-900">KYC Identity Management</h1>
              <p className="text-amber-700 text-sm">Track and manage linked accounts</p>
            </div>
          </div>
          <Button onClick={() => { fetchKYCIdentities(); fetchAlerts(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-900">{alerts.length} New Alert(s)</span>
              </div>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert) => (
                  <div key={alert.alert_id} className="flex items-center justify-between bg-white rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium">{alert.message}</p>
                      <p className="text-xs text-gray-500">Aadhaar: ****{alert.data?.aadhaar_last_4}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => fetchKYCDetail(alert.data?.kyc_identity_id)}>
                        View
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => markAlertRead(alert.alert_id)}>
                        Dismiss
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Users className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Multi-Account KYCs</p>
                <p className="text-2xl font-bold text-amber-900">{stats.multi_account_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unread Alerts</p>
                <p className="text-2xl font-bold text-yellow-900">{alerts.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <ShieldCheck className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total KYCs Shown</p>
                <p className="text-2xl font-bold text-green-900">{pagination.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-center">
              <Button 
                onClick={handleRunBackfill} 
                disabled={backfillLoading}
                variant="outline"
                className="w-full"
              >
                {backfillLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running Backfill...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Run KYC Backfill
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for KYC and PAN */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="kyc" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              KYC Identities
            </TabsTrigger>
            <TabsTrigger value="pan" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              PAN Verifications
            </TabsTrigger>
          </TabsList>

          {/* KYC Tab Content */}
          <TabsContent value="kyc" className="space-y-4">
            {/* Filters */}
            <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-gray-600 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Name, Phone or Aadhaar last 4..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-8"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              
              <div className="w-40">
                <label className="text-sm text-gray-600 mb-1 block">Status</label>
                <Select value={statusFilter || 'all'} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="multiOnly"
                  checked={multiAccountOnly}
                  onChange={(e) => setMultiAccountOnly(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="multiOnly" className="text-sm">Multi-account only</label>
              </div>
              
              <Button type="submit">Search</Button>
            </form>
            {activeSearch && (
              <div className="mt-2 text-sm text-gray-500">
                Showing results for: "<span className="font-medium">{activeSearch}</span>"
                <button onClick={handleClearSearch} className="ml-2 text-blue-600 hover:underline">Clear</button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* KYC List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">KYC Identities</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : kycIdentities.length === 0 ? (
                <div className="text-center py-8">
                  {activeSearch ? (
                    <div className="text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No KYC identities found matching "<span className="font-medium">{activeSearch}</span>"</p>
                      <button onClick={handleClearSearch} className="mt-2 text-blue-600 hover:underline text-sm">Clear search</button>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No KYC Identities Found</p>
                      <p className="text-sm mt-1 max-w-xs mx-auto">
                        KYC identities are created when users complete Aadhaar verification. 
                        Click "Run KYC Backfill" above to process existing users.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {kycIdentities.map((kyc) => (
                    <div
                      key={kyc.kyc_identity_id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedKYC === kyc.kyc_identity_id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                      onClick={() => fetchKYCDetail(kyc.kyc_identity_id)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">Aadhaar: ****{kyc.aadhaar_last_4}</p>
                          <p className="text-sm text-gray-500">
                            {kyc.total_accounts} account(s) • {kyc.active_accounts} active
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {getStatusBadge(kyc.account_status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detail Panel */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">KYC Details</CardTitle>
            </CardHeader>
            <CardContent>
              {detailLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-20" />
                  <Skeleton className="h-32" />
                  <Skeleton className="h-24" />
                </div>
              ) : kycDetail ? (
                <div className="space-y-6">
                  {/* Status & Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(kycDetail.kyc_identity?.account_status)}
                    </div>
                    <div className="flex gap-2">
                      {kycDetail.kyc_identity?.account_status !== 'blocked' && (
                        <Button size="sm" variant="outline" onClick={() => setRestrictDialog(true)}>
                          <ShieldAlert className="w-4 h-4 mr-1" />
                          {kycDetail.kyc_identity?.account_status === 'restricted' ? 'Block' : 'Restrict'}
                        </Button>
                      )}
                      {kycDetail.kyc_identity?.account_status === 'blocked' && (
                        <Button size="sm" variant="outline" onClick={handleUnblock} disabled={actionLoading}>
                          <Unlock className="w-4 h-4 mr-1" />
                          Unblock
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Aadhaar */}
                  <div>
                    <p className="text-sm text-gray-500">Aadhaar (Masked)</p>
                    <p className="font-mono text-lg">{kycDetail.kyc_identity?.aadhaar_masked || `****-****-${kycDetail.kyc_identity?.aadhaar_last_4}`}</p>
                  </div>

                  {/* Linked Accounts */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Linked Accounts ({kycDetail.kyc_identity?.total_accounts})</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {kycDetail.kyc_identity?.linked_accounts?.map((acc, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{acc.name || 'Unknown'}</span>
                            <span className="text-gray-400">{acc.phone}</span>
                          </div>
                          <Badge variant={acc.status === 'active' ? 'default' : 'secondary'}>
                            {acc.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cumulative Totals */}
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Cumulative Totals (All Accounts)</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-green-50 rounded">
                        <p className="text-xs text-gray-500">Total Bought</p>
                        <p className="font-semibold">{formatCurrency(kycDetail.kyc_identity?.cumulative?.total_buy_inr || 0)}</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded">
                        <p className="text-xs text-gray-500">Total Refunded</p>
                        <p className="font-semibold">{formatCurrency(kycDetail.kyc_identity?.cumulative?.total_sell_inr || 0)}</p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="text-xs text-gray-500">Total Transactions</p>
                        <p className="font-semibold">{kycDetail.kyc_identity?.cumulative?.total_transactions || 0}</p>
                      </div>
                      <div className="p-2 bg-amber-50 rounded">
                        <p className="text-xs text-gray-500">Deletions</p>
                        <p className="font-semibold">{kycDetail.kyc_identity?.cumulative?.account_deletions || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">Admin Notes</p>
                      <Button size="sm" variant="ghost" onClick={() => setNoteDialog(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    {kycDetail.kyc_identity?.admin_notes?.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {kycDetail.kyc_identity.admin_notes.map((note, i) => (
                          <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                            <p>{note.note}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {note.by} • {note.at ? formatDateTimeIST(note.at) : 'Unknown date'}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No notes yet</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a KYC identity to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* PAN Verifications Tab Content */}
          <TabsContent value="pan" className="space-y-4">
            {/* PAN Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm text-gray-600 mb-1 block">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Name or Phone..."
                        value={panSearchQuery}
                        onChange={(e) => setPanSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <label className="text-sm text-gray-600 mb-1 block">Status</label>
                    <Select value={panStatusFilter} onValueChange={setPanStatusFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchPanList} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* PAN List and Detail */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* PAN List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    PAN Submissions ({panPagination.total})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {panLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
                    </div>
                  ) : panList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No PAN submissions found</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {panList.map((pan) => (
                        <div
                          key={pan.user_id}
                          className={`p-4 border rounded-lg cursor-pointer hover:border-amber-300 transition-colors ${
                            selectedPan?.user_id === pan.user_id ? 'border-amber-500 bg-amber-50' : ''
                          }`}
                          onClick={() => {
                            setSelectedPan(pan);
                            handleViewPanPhoto(pan.user_id);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{pan.name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500">{pan.phone}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                PAN: XXXXXX{pan.pan_last_4} • Submitted: {formatDateTimeIST(pan.pan_submitted_at)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {getPanStatusBadge(pan.pan_status)}
                              {pan.total_successful_buy_inr > 0 && (
                                <span className="text-xs text-gray-500">
                                  Total: {formatCurrency(pan.total_successful_buy_inr)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* PAN Pagination */}
                  {panPagination.pages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={panPagination.page === 1}
                        onClick={() => setPanPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        <ChevronLeft className="w-4 h-4" /> Prev
                      </Button>
                      <span className="text-sm text-gray-500">
                        Page {panPagination.page} of {panPagination.pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={panPagination.page === panPagination.pages}
                        onClick={() => setPanPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        Next <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* PAN Detail Panel */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">PAN Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedPan ? (
                    <div className="text-center py-12 text-gray-400">
                      <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a PAN submission to view details</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* User Info */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{selectedPan.name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{selectedPan.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-gray-400" />
                          <span className="font-mono">XXXXXX{selectedPan.pan_last_4}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Wallet className="w-4 h-4 text-gray-400" />
                          <span>Total Purchases: {formatCurrency(selectedPan.total_successful_buy_inr || 0)}</span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Status</p>
                        {getPanStatusBadge(selectedPan.pan_status)}
                        {selectedPan.pan_status === 'rejected' && selectedPan.pan_rejection_reason && (
                          <p className="text-sm text-red-600 mt-2">
                            Reason: {selectedPan.pan_rejection_reason}
                          </p>
                        )}
                      </div>

                      {/* PAN Photo */}
                      <div>
                        <p className="text-sm text-gray-600 mb-2">PAN Card Photo</p>
                        <div className="border rounded-lg overflow-hidden bg-gray-100 min-h-[150px] flex items-center justify-center">
                          {panPhotoLoading ? (
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                          ) : panPhotoUrl ? (
                            <img 
                              src={panPhotoUrl.startsWith('data:') ? panPhotoUrl : `data:image/jpeg;base64,${panPhotoUrl}`}
                              alt="PAN Card"
                              className="max-w-full max-h-[300px] object-contain"
                            />
                          ) : (
                            <div className="text-gray-400 text-center p-4">
                              <Image className="w-8 h-8 mx-auto mb-2" />
                              <p className="text-sm">No photo available</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {selectedPan.pan_status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleApprovePan(selectedPan.user_id)}
                            disabled={panActionLoading}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            {panActionLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => setRejectPanDialog(true)}
                            disabled={panActionLoading}
                            variant="destructive"
                            className="flex-1"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      )}

                      {selectedPan.pan_status === 'rejected' && (
                        <Button
                          onClick={() => handleApprovePan(selectedPan.user_id)}
                          disabled={panActionLoading}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          Re-approve PAN
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Restrict/Block Dialog */}
        <Dialog open={restrictDialog} onOpenChange={setRestrictDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {kycDetail?.kyc_identity?.account_status === 'restricted' ? 'Block' : 'Restrict'} KYC Identity
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Reason *</label>
                <Select value={restrictReason} onValueChange={setRestrictReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suspicious_activity">Suspicious Activity</SelectItem>
                    <SelectItem value="aml_breach">AML Breach</SelectItem>
                    <SelectItem value="fraud">Fraud</SelectItem>
                    <SelectItem value="compliance">Compliance Issue</SelectItem>
                    <SelectItem value="manual">Manual Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Details (Optional)</label>
                <Textarea
                  value={restrictDetails}
                  onChange={(e) => setRestrictDetails(e.target.value)}
                  placeholder="Additional details..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestrictDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => handleRestrict(kycDetail?.kyc_identity?.account_status === 'restricted')}
                disabled={actionLoading}
                className={kycDetail?.kyc_identity?.account_status === 'restricted' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {actionLoading ? 'Processing...' : (kycDetail?.kyc_identity?.account_status === 'restricted' ? 'Block' : 'Restrict')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Note Dialog */}
        <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Admin Note</DialogTitle>
            </DialogHeader>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note..."
              rows={4}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
              <Button onClick={handleAddNote} disabled={actionLoading}>
                {actionLoading ? 'Adding...' : 'Add Note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PAN Rejection Dialog */}
        <Dialog open={rejectPanDialog} onOpenChange={setRejectPanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Reject PAN Submission
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this PAN submission. The user will be notified and can resubmit.
              </p>
              <div>
                <label className="text-sm font-medium">Rejection Reason *</label>
                <Select value={rejectPanReason} onValueChange={setRejectPanReason}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Image not clear">Image not clear</SelectItem>
                    <SelectItem value="PAN number mismatch">PAN number mismatch</SelectItem>
                    <SelectItem value="Invalid PAN format">Invalid PAN format</SelectItem>
                    <SelectItem value="Name mismatch with account">Name mismatch with account</SelectItem>
                    <SelectItem value="Document appears tampered">Document appears tampered</SelectItem>
                    <SelectItem value="Other">Other (specify in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRejectPanDialog(false);
                setRejectPanReason('');
              }}>Cancel</Button>
              <Button 
                onClick={handleRejectPan}
                disabled={panActionLoading || !rejectPanReason}
                variant="destructive"
              >
                {panActionLoading ? 'Rejecting...' : 'Reject PAN'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminKYCIdentitiesPage;
